import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAdmin } from '../adminContext';
import { overviewApi, todayRange } from './overviewApi';

/** Query key of store connections at HO; one store's is this plus its id. */
export const connectionKey = ['ho', 'sync', 'connections'] as const;
/** Query key of the store's own link to HO (ADR 0014). */
export const syncStatusKey = ['store', 'sync', 'status'] as const;
/** Every overview widget's query key starts with this, so one call refreshes them all. */
export const overviewKey = ['overview'] as const;

/** One overview widget's data: polled while the tab is visible, never in the background. */
export function useOverviewQuery<T>(key: readonly unknown[], fn: () => Promise<T>, interval: number, enabled = true) {
  return useQuery({
    queryKey: [...overviewKey, ...key],
    queryFn: fn,
    refetchInterval: interval,
    refetchIntervalInBackground: false,
    enabled,
  });
}

/** Every store's connection to HO and its health, refreshed every 30 seconds (ADR 0014). */
export function useStoreConnections() {
  const { client } = useAdmin();
  return useQuery({
    queryKey: connectionKey,
    queryFn: () => overviewApi(client).stores(),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

/** This store server's link to HO, refreshed every 15 seconds. Open without login (ADR 0014). */
export function useSyncStatus() {
  const { client } = useAdmin();
  return useQuery({
    queryKey: syncStatusKey,
    queryFn: () => overviewApi(client).syncStatus(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

/** Refresh every overview widget now. */
export function useRefreshOverview() {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all(
      [overviewKey, connectionKey, syncStatusKey].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  }, [queryClient]);
}

export type OverviewMode = 'ho' | 'store';

const MINUTE = 60_000;

/** The overview calls for the current portal's client. */
export function useOverviewApi() {
  const { client } = useAdmin();
  return useMemo(() => overviewApi(client), [client]);
}

/** Sales figures for today: HO asks for the local day; the store answers for its open business day. */
export function useSalesSummary(mode: OverviewMode) {
  const api = useOverviewApi();
  return useOverviewQuery(['summary', mode], () => api.summary(mode === 'ho' ? todayRange() : {}), MINUTE);
}
